import { Component, inject, OnDestroy, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { Store } from '@ngrx/store';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { TransactionHelper } from '@scifamek/guadua';
import { Subscription } from 'rxjs';

import AppState from '../../../app.state';
import { LoginCommand } from '../../../features/auth/state-management/auth.commands';
import { UserKindSelectedEvent } from '../../../features/auth/state-management/auth.events';
import { selectUsers, selectLoginError, selectLoading } from '../../../features/auth/state-management/auth.selectors';
import { UserKindDialog, UserKindDialogResult } from './user-kind-dialog';
import { I18nService, LanguageSelector } from '../../i18n';

@Component({
  selector: 'app-login',
  templateUrl: './login.html',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatFormFieldModule,
    MatInputModule,
    MatButtonModule,
    MatIconModule,
    MatProgressSpinnerModule,
    MatDialogModule,
    LanguageSelector,
  ],
  styleUrls: ['./login.scss'],
})
export class Login implements OnInit, OnDestroy {
  static readonly route = 'login';

  private readonly store = inject(Store<AppState>);
  private readonly transactionHelper = inject(TransactionHelper);
  private readonly dialog = inject(MatDialog);
  readonly i18n = inject(I18nService);

  formGroup = new FormGroup({
    email: new FormControl('', [Validators.required, Validators.email]),
    password: new FormControl('', [Validators.required, Validators.minLength(6)]),
  });

  loginError$ = this.store.select(selectLoginError);
  loading$ = this.store.select(selectLoading);
  showPassword = false;
  currentYear = new Date().getFullYear();

  private usersSubscription?: Subscription;

  ngOnInit(): void {
    this.usersSubscription = this.store.select(selectUsers).subscribe((users) => {
      if (users.length > 1) {
        const dialogRef = this.dialog.open(UserKindDialog, {
          data: { users, authId: '' },
          disableClose: true,
        });

        dialogRef.afterClosed().subscribe((result?: UserKindDialogResult) => {
          if (result) {
            this.store.dispatch(
              UserKindSelectedEvent({ userKind: result.userKind, authId: result.authId })
            );
          }
        });
      }
    });
  }

  ngOnDestroy(): void {
    this.usersSubscription?.unsubscribe();
  }

  login(): void {
    if (this.formGroup.valid) {
      const { email, password } = this.formGroup.getRawValue();
      const transactionId = this.transactionHelper.identifier({ email, password });
      this.store.dispatch(
        LoginCommand({ username: email!, password: password!, transactionId })
      );
    }
  }
}
